import { ActionIcon, Alert, Card, Group, Loader, Modal, Switch, Text, Tooltip } from "@mantine/core";
import { MultiViewer } from "@multiconsult-group/multiplacer-ui";
import { skipToken } from "@reduxjs/toolkit/query";
import { IconMaximize, IconX } from "@tabler/icons-react";
import React, { useState } from "react";

import { useGetMcbimItemsInSpaceQuery, useGetMcbimSpaceQuery } from "../../../../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import {
  selectClickedRoomNumberInModal,
  selectFloorplanRotation,
  selectFloorplanIssueBoardId,
  selectMcbimItemsInModalRoomArgs,
  selectMcbimSpaceInModalArgs,
  selectPixPathsForModalRoomCeilingGrids,
  selectShowLengthItemsInSelectedRoom,
  selectViewerPixPolygonsForModalRoom,
} from "../../Floorplan/floorplanSelectors";
import {
  setClickedRoomNumberInModalInRedux,
  setShowLengthItemsInSelectedRoomInRedux,
} from "../../Floorplan/floorplanSlice";

const IssueModalRoomViewer = () => {
  const [fullscreenOpened, setFullscreenOpened] = useState(false);
  const dispatch = useAppDispatch();
  const issueBoardId = useAppSelector(selectFloorplanIssueBoardId);
  const clickedRoomNumberInModal = useAppSelector(selectClickedRoomNumberInModal);
  const mcbimSpaceInModalArgs = useAppSelector(selectMcbimSpaceInModalArgs);
  const mcbimItemsInModalRoomArgs = useAppSelector(selectMcbimItemsInModalRoomArgs);
  const floorplanRotation = useAppSelector(selectFloorplanRotation);
  const showLengthItemsInSelectedRoom = useAppSelector(selectShowLengthItemsInSelectedRoom);
  const polygons = useAppSelector(selectViewerPixPolygonsForModalRoom);
  const paths = useAppSelector(selectPixPathsForModalRoomCeilingGrids);
  const { isFetching: isFetchingSpace, isError: isSpaceError } = useGetMcbimSpaceQuery(
    mcbimSpaceInModalArgs ?? skipToken,
  );
  const { isFetching: isFetchingItems, isError: isItemsError } = useGetMcbimItemsInSpaceQuery(
    mcbimItemsInModalRoomArgs ?? skipToken,
  );
  const isFetching = isFetchingSpace || isFetchingItems;
  const isError = isSpaceError || isItemsError;

  const clearSelectedRoom = () => {
    if (issueBoardId == null) return;

    setFullscreenOpened(false);
    dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: null }));
  };

  if (clickedRoomNumberInModal == null) {
    return null;
  }

  return (
    <>
      <Card withBorder radius="md" p="md" mt="sm">
        <Group justify="space-between" align="center" gap="xs">
          <Text fw={600}>{clickedRoomNumberInModal}</Text>
          <Group gap="sm" wrap="nowrap">
            <Switch
              size="sm"
              label="Vis lengdeobjekter"
              checked={showLengthItemsInSelectedRoom}
              onChange={(event) => {
                if (issueBoardId == null) return;

                dispatch(
                  setShowLengthItemsInSelectedRoomInRedux({
                    issueBoardId,
                    showLengthItemsInSelectedRoom: event.currentTarget.checked,
                  }),
                );
              }}
            />
            {polygons.length > 0 && (
              <Tooltip label="Vis i fullskjerm" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Vis romviewer i fullskjerm"
                  onClick={() => setFullscreenOpened(true)}
                >
                  <IconMaximize size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Fjern valgt rom" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Fjern valgt rom fra romviewer"
                onClick={clearSelectedRoom}
              >
                <IconX size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        {isFetching && <Loader size="sm" mt="xs" />}
        {isError && (
          <Alert color="red" mt="xs">
            Klarte ikke å hente rommet.
          </Alert>
        )}
        {!isFetching && !isError && polygons.length === 0 && (
          <Text c="dimmed" size="sm" mt="xs">
            Rommet mangler polygon.
          </Text>
        )}
        {polygons.length > 0 && (
          <div style={{ height: 320, marginTop: "0.75rem" }}>
            <LocalErrorBoundary id="IssueModalRoomViewerInline">
              <MultiViewer
                twoDOnly
                twoD={{
                  polygons,
                  paths,
                  angle: floorplanRotation,
                  zoomExtentKey: clickedRoomNumberInModal,
                }}
                threeD={{
                  fragFiles: [],
                  visibleMcbimFileNames: [],
                }}
              />
            </LocalErrorBoundary>
          </div>
        )}
      </Card>

      {polygons.length > 0 && (
        <Modal
          opened={fullscreenOpened}
          onClose={() => setFullscreenOpened(false)}
          title={
            <Group gap="xs" wrap="nowrap">
              <Text fw={600}>{clickedRoomNumberInModal}</Text>
              <Tooltip label="Fjern valgt rom" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Fjern valgt rom fra romviewer"
                  onClick={clearSelectedRoom}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          }
          fullScreen
          styles={{
            body: {
              height: "calc(100vh - 4.5rem)",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <div style={{ flex: 1, minHeight: 0 }}>
            <LocalErrorBoundary id="IssueModalRoomViewerFullscreen">
              <MultiViewer
                twoDOnly
                twoD={{
                  polygons,
                  paths,
                  angle: floorplanRotation,
                  zoomExtentKey: `${clickedRoomNumberInModal}-fullscreen`,
                }}
                threeD={{
                  fragFiles: [],
                  visibleMcbimFileNames: [],
                }}
              />
            </LocalErrorBoundary>
          </div>
        </Modal>
      )}
    </>
  );
};

export default IssueModalRoomViewer;
